import Link from '@components/common/Link/Link'
import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Spinner from '@components/icons/Spinner/Spinner'

import Media from '@components/ui/Media/Media'
import { refineProductTitle } from '@components/views/Product/helpersv2'

import { useCheckoutOrderById } from '@features/checkout/api/get-order-by-id'
import { getTrackingProductId } from '@features/products/utils'
import { centAmountToAmountV2, formatPriceV2 } from '@lib/format-price'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { generateMediaUrlV2 } from '@lib/utils'
import { selectCountry } from '@redux/globalSlice'
import { mapEdgesToItems } from '@utils/map'
import {
  IEcommerceProduct,
  pushPurchaseTrackingEvent,
  removeSpecialCharactersFromPhoneNumber,
} from '@utils/tracking-utils'
import clsx from 'clsx'
import { FC, useEffect } from 'react'
import { useSelector } from 'react-redux'
import s from './CheckoutConfirmation.module.scss'
interface CheckoutConfirmationProps {
  orderId: string
}

const CheckoutConfirmation: FC<CheckoutConfirmationProps> = ({ orderId }) => {
  const country = useSelector(selectCountry)
  const { data, isLoading } = useCheckoutOrderById({ orderId })

  const order = data?.order

  const loading = isLoading || !order

  /* ---- GTM Tracking Events ----- */
  useEffect(() => {
    if (!order) return

    pushPurchaseTrackingEvent({
      id: order.id,
      currencyCode: order.total.currencyCode.toUpperCase(),
      // ...(order?.voucherCode ? { coupon: order?.voucherCode } : {}), // coupon: order?.voucherCode, // TODO: add coupon code (if any
      revenue: centAmountToAmountV2(order.total).toString(),
      shipping: centAmountToAmountV2(
        order.shippingLines.find(Boolean)?.total,
      ).toString(),
      tax: centAmountToAmountV2(order.taxedPrice.tax).toString(),
      products: mapEdgesToItems(order.lineItems).map((lineItem) => {
        const unitPrice =
          lineItem.unitPrice.discountedPrice?.value ?? lineItem.unitPrice.value
        const totalDiscount = mapEdgesToItems(
          lineItem.discountApplications,
        ).reduce(
          (acc, discount) => acc + discount.discountedAmount.centAmount,
          0,
        )
        return {
          id: getTrackingProductId(
            lineItem.product.id,
            lineItem.product.metadata,
          ),
          name: lineItem.productName,
          variant: lineItem.variantName,
          category:
            lineItem.product.tags.length > 0 &&
            lineItem.product.tags
              ?.find((tag) => tag?.startsWith('type_'))
              ?.split('_')[1]
              .replace(/-/g, ''),
          brand: lineItem.product.vendor,
          quantity: lineItem.quantity,
          price: centAmountToAmountV2(unitPrice).toString(),
          discount: centAmountToAmountV2({
            centAmount: totalDiscount,
            currencyCode: lineItem.unitPrice.value.currencyCode,
            fractionDigits: lineItem.unitPrice.value.fractionDigits,
          }).toString(),
          dimension1: lineItem.sku.toString(),
        } as IEcommerceProduct
      }),
      userData: {
        email: order.shippingAddress.email,
        firstName: order.shippingAddress.firstName,
        lastName: order.shippingAddress.lastName,
        phone: removeSpecialCharactersFromPhoneNumber(
          order.shippingAddress.phone,
        ),
        city: order.shippingAddress.city,
        state: order.shippingAddress.state,
        country: order.shippingAddress.countryCode,
        postalCode: order.shippingAddress.postalCode,
      },
    })
  }, [order])

  if (loading)
    return (
      <div className="flex justify-center">
        <Spinner style={{ width: '50px', height: '50px', color: 'black' }} />
      </div>
    )

  if (!order) return <div>Order not found</div>

  const totalDiscount = mapEdgesToItems(order.lineItems).reduce(
    (acc, item) =>
      acc +
      mapEdgesToItems(item.discountApplications).reduce(
        (acc2, discount) => acc2 + discount.discountedAmount.centAmount,
        0,
      ),
    0,
  )

  const taxPercent = Number(
    Number(
      (order.taxedPrice.gross.centAmount / order.taxedPrice.net.centAmount -
        1) *
        100,
    ).toFixed(2),
  )
  const taxAmount =
    order.taxedPrice.gross.centAmount - order.taxedPrice.net.centAmount

  return (
    <div className={s.root}>
      <div className={clsx(s.section, s['col-4'])}>
        <div className={s.block}>
          <div className={s.block__title}>Date</div>
          {new Date(order.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
        <div className={s.block}>
          {/* <div className={s.block__title}>Order Number</div>
          ECO{order.number.toString().padStart(5, '0')} */}
        </div>
        <div className={s.block}>
          <div className={s.block__title}>Billing Address</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {order.billingAddress.formatted}
          </div>
        </div>
        <div className={s.block}>
          <div className={s.block__title}>Shipping Address</div>
          <TrustedHtml
            html={order.shippingAddress.formatted.replaceAll('\n', '<br />')}
          />
          {/* {order.shippingAddress.phone && <div>Phone: {order.billingAddress.phone}</div>}
                      {order.shippingAddress.email && <div>Email: {order.billingAddress.email}</div>} */}
        </div>
      </div>

      <div className={clsx(s.section, s['col-1'])}>
        <div className={s.item__list__header}>
          {/* <div className={s.item__placeholder}></div> */}
          <div className={s.item__title}>Product</div>
          <div className={s.item__quantity}>Qty</div>
          <div className={s.item__price}>Unit Price</div>
        </div>
        {mapEdgesToItems(order.lineItems).map((item) => {
          const isSalePrice = !!item.unitPrice.discountedPrice

          return (
            <div key={item.id} className={s.item}>
              <div className={s.img}>
                {item.variant?.image.src ? (
                  <Media
                    mediaWithCrops={
                      {
                        url: generateMediaUrlV2(item.variant.image?.src),
                        media: {
                          __typename: 'Image',
                          url: generateMediaUrlV2(item.variant.image?.src),
                        },
                      } as MediaWithCropsFragment
                    }
                    mediaClassName={s.image}
                  />
                ) : (
                  <div className={s.noImage}>{item.variantName}</div>
                )}
              </div>
              {/* <div className={s.item__content}> */}
              <div className={s.item__title}>
                <Link
                  className={s.link}
                  link={
                    item.variant
                      ? {
                          url: `/products/${item.productSlug}`,
                          target: '_new',
                        }
                      : null
                  }
                  query={item.variant ? { itemNumber: item.sku } : null}
                >
                  <span className={s.underlineAnimation}>
                    {item.productName}
                  </span>
                  <div className={s.item__subtitles}>
                    <p>{refineProductTitle(item.variantName).secondaryName}</p>
                    <p>Item Number: {item.sku}</p>
                  </div>
                  <div className={clsx(s.item__subtitles, s.mobileOnly)}>
                    <p>
                      <span className="mr-1">Qty: {item.quantity} x </span>
                      {isSalePrice && (
                        <span className={clsx(s.item__original__subtotal)}>
                          {formatPriceV2(
                            country.code,
                            {
                              centAmount: item.unitPrice.value.centAmount,
                              currencyCode: item.unitPrice.value.currencyCode,
                              fractionDigits:
                                item.unitPrice.value.fractionDigits,
                            },
                            2,
                          )}
                        </span>
                      )}
                      <span className={clsx({ [s.salePrice]: isSalePrice })}>
                        {formatPriceV2(
                          country.code,
                          item.unitPrice.discountedPrice?.value ??
                            item.unitPrice.value,
                          2,
                        )}
                      </span>
                    </p>
                  </div>
                </Link>
              </div>

              {/* <div className={s.item__totals}> */}
              <div className={s.item__quantity}>{item.quantity}</div>
              <div className={s.item__price}>
                {isSalePrice && (
                  <span className={s.item__original__subtotal}>
                    {formatPriceV2(
                      country.code,
                      {
                        centAmount: item.unitPrice.value.centAmount,
                        currencyCode: item.total.currencyCode,
                        fractionDigits: item.total.fractionDigits,
                      },
                      2,
                    )}
                  </span>
                )}
                <span
                  className={clsx(s.item__subtotal, {
                    [s.salePrice]: isSalePrice,
                  })}
                >
                  {formatPriceV2(
                    country.code,
                    item.unitPrice.discountedPrice?.value ??
                      item.unitPrice.value,
                    2,
                  )}
                </span>
              </div>
              {/* </div> */}
              {/* </div> */}
            </div>
          )
        })}
      </div>

      <div className={clsx(s.section, s.short__section)}>
        <div className={clsx(s.block, s.block__row)}>
          <div>Subtotal</div>
          <div>
            {formatPriceV2(
              country.code,
              {
                centAmount: order.subtotal.centAmount,
                currencyCode: order.subtotal.currencyCode,
                fractionDigits: order.subtotal.fractionDigits,
              },
              2,
            )}
          </div>
        </div>
        <div className={clsx(s.block, s.block__row)}>
          <div>Delivery</div>
          <div>
            {order.shippingLines.find(Boolean)?.total.centAmount > 0
              ? formatPriceV2(
                  country.code,
                  order.shippingLines.find(Boolean)?.total,
                  2,
                )
              : 'Free'}
          </div>
        </div>
        {taxAmount > 0 && (
          <div className={clsx(s.block, s.block__row)}>
            <div>
              {order.shippingAddress.countryCode == 'US' ? 'Tax' : 'VAT'} (
              {taxPercent}%)
            </div>
            <div>
              {formatPriceV2(
                country.code,
                {
                  centAmount: taxAmount,
                  currencyCode: order.taxedPrice.gross.currencyCode,
                  fractionDigits: order.taxedPrice.gross.fractionDigits,
                },
                2,
              )}
            </div>
          </div>
        )}
        {totalDiscount > 0 && (
          <div className={clsx(s.block, s.block__row)}>
            <div>Discount</div>
            <div className={s.discount}>
              {formatPriceV2(
                country.code,
                {
                  centAmount: -1 * totalDiscount,
                  currencyCode: order.total.currencyCode,
                  fractionDigits: order.total.fractionDigits,
                },
                2,
              )}
            </div>
          </div>
        )}
        <div className={clsx(s.block, s.block__row, s.bold)}>
          <div>Total</div>
          <div>
            {formatPriceV2(
              country.code,
              {
                centAmount: order.total.centAmount,
                currencyCode: order.total.currencyCode,
                fractionDigits: order.total.fractionDigits,
              },
              2,
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

CheckoutConfirmation.displayName = 'CheckoutConfirmation'
export default CheckoutConfirmation
